"use client";

import React, { useEffect, useRef } from "react";
import Image from "next/image";

const steps = [
  {
    number: "01",
    title: "Source",
    description:
      "Oats from certified gluten-free mills and pea protein from Canadian farms.",
    image: "/assets/images/how_its_made_source.png",
    alt: "Golden oat grains and wheat scattered on a warm cream linen surface representing ingredient sourcing",
  },
  {
    number: "02",
    title: "Craft",
    description:
      "Small-batch baked in our Montréal kitchen facility. No extrusion, no industrial processing — just real baking.",
    image: "/assets/images/how_its_made_craft.png",
    alt: "Hands shaping cookie dough on a wooden surface surrounded by oats and flour",
  },
  {
    number: "03",
    title: "Test",
    description:
      "Each batch is tested for quality control and verifying freshness before hand-packaging each cookie. ",
    image: "/assets/images/QC_test_PickMeUp_compressed-1774450553875.jpg",
    alt: "A thick golden-brown chickpea and oat chocolate chip cookie next to a white Pick Me Up box labeled Chocolate Chip and a light blue individual wrapper with Pick Me Up branding",
  },
];

export default function HowItsMadeSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("is-visible");
        });
      },
      { threshold: 0.1 }
    );
    sectionRef?.current?.querySelectorAll(".reveal-hidden")?.forEach((el) =>
      observer?.observe(el)
    );
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="how-its-made"
      ref={sectionRef}
      className="bg-pmu-warm py-24 px-6 md:px-12"
    >
      {/* Header */}
      <div className="max-w-3xl mx-auto text-center mb-16 reveal-hidden">
        <p
          className="text-xs font-semibold uppercase tracking-widest mb-4"
          style={{ color: "#C4B89A", letterSpacing: "0.2em" }}
        >
          How It&apos;s Made
        </p>
        <h2
          className="font-fraunces font-bold text-pmu-espresso leading-tight"
          style={{ fontSize: "clamp(2rem, 4.5vw, 3.2rem)" }}
        >
          From farm to{" "}
          <span className="italic font-light" style={{ color: "#A8C4D8" }}>
            your gym bag.
          </span>
        </h2>
        <p className="mt-5 text-base text-pmu-taupe max-w-lg mx-auto leading-relaxed">
          We like to understand what we are putting in our bodies. Here&apos;s how every
          Pick Me Up cookie gets made.
        </p>
      </div>
      {/* Steps */}
      <div className="max-w-5xl mx-auto">
        {/* Connector line — desktop only */}
        <div className="hidden lg:block relative mb-0">
          <div
            className="absolute top-6 left-[16.5%] right-[16.5%] h-px"
            style={{ backgroundColor: "#D6CFC4" }}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-6">
          {steps?.map((step, index) => (
            <div
              key={step?.number}
              className={`flex flex-col items-center text-center reveal-hidden delay-${index + 1}`}
            >
              {/* Step Image */}
              <div
                className="w-full mb-5 overflow-hidden rounded-xl"
                style={{ aspectRatio: "4/3" }}
              >
                <Image
                  src={step?.image}
                  alt={step?.alt}
                  width={320}
                  height={240}
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                />
              </div>

              {/* Number circle */}
              <div
                className="relative z-10 w-12 h-12 rounded-full flex items-center justify-center mb-5 flex-shrink-0"
                style={{
                  backgroundColor: "#FAFAF8",
                  border: "1.5px solid #D6CFC4",
                }}
              >
                <span
                  className="font-fraunces font-bold text-sm"
                  style={{ color: "#A8C4D8" }}
                >
                  {step?.number}
                </span>
              </div>

              {/* Title */}
              <h3
                className="font-fraunces font-bold text-pmu-espresso text-lg mb-2"
              >
                {step?.title}
              </h3>

              {/* Description */}
              <p className="text-sm text-pmu-taupe leading-relaxed">
                {step?.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
